import type { LocationRewrite } from "@tanstack/router-core";

import {
  baseLocale,
  deLocalizeHref,
  defineCustomClientStrategy,
  defineCustomServerStrategy,
  deLocalizeUrl,
  locales,
  localizeHref,
  localizeUrl,
  type Locale,
} from "@/paraglide/runtime.js";

/**
 * English lives at plain URLs and every other language under its prefix (`/pl/...`), the way
 * Gravl does it (ADR 0026). A prefix is an explicit choice - a link somebody shared, a click on
 * the language picker - so it beats anything saved. A plain URL is ambiguous: it is the English
 * page, but it is also every link in an email. There the saved language and then the browser's
 * decide, and the server redirects to `/pl/...` when they say Polish. Crawlers carry neither, so
 * they get English at the plain URL and find each other language through hreflang.
 */
export const localeFromPathname = (pathname: string): Locale | undefined => {
  const prefix = pathname.split("/")[1];
  return locales.find((locale) => locale !== baseLocale && locale === prefix);
};

// The URL itself is the record of the choice, so there is nothing for these to store.
defineCustomServerStrategy("custom-prefix", {
  getLocale: (request) => (request ? localeFromPathname(new URL(request.url).pathname) : undefined),
});
defineCustomClientStrategy("custom-prefix", {
  getLocale: () => (typeof window === "undefined" ? undefined : localeFromPathname(window.location.pathname)),
  setLocale: () => undefined,
});

/**
 * The router only knows unprefixed paths. Incoming URLs lose their prefix before matching, and
 * every href the router builds - `<Link>`, `navigate`, `redirect` - gets the current one back, so
 * no route or link has to mention a language.
 */
export const localeRewrite: LocationRewrite = {
  input: ({ url }) => deLocalizeUrl(url),
  output: ({ url }) => {
    const localized = localizeUrl(url);
    // Paraglide puts a language's home at `/pl/`; it is `/pl`, like every other page without the slash.
    if (localeFromPathname(localized.pathname) && localized.pathname.endsWith("/")) {
      localized.pathname = localized.pathname.slice(0, -1);
    }
    return localized;
  },
};

/** `path` (unprefixed, starting with `/`) as it reads in `locale`. */
export const localizePath = (path: string, locale: Locale): string => {
  if (locale === baseLocale) return path;
  return path === "/" ? `/${locale}` : `/${locale}${path}`;
};

/** The sign-in page in the current language, coming back to the page the visitor is on. */
export const signInHref = (): string =>
  localizeHref(`/login?redirect=${encodeURIComponent(deLocalizeHref(window.location.pathname))}`);
